import { type Currency } from '@principle-theorem/accounting';
import {
  ISoftDelete,
  TypeGuard,
  isDocRef,
  isObject,
  isTimestamp,
  type DocumentReference,
  type Timestamp,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import { type IAppointment } from '../appointment/appointment';
import { type IAppointmentRequest } from '../appointment/appointment-request';
import { type ISchedulingEvent } from '../appointment/cancellation';
import { type IInvoice } from '../invoice/invoice';
import { type IPatient } from '../patient/patient';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import {
  DepositCaptureStrategy,
  type IDepositOptionText,
} from './deposit-capture-strategy';
import { type IDepositPaymentAttempt } from './deposit-payment-attempt';
import { DepositPaymentMode } from './deposit-payment-mode';
import {
  isDepositTreatmentConfigurationContributionMap,
  type IDepositTreatmentConfigurationContribution,
} from './deposit-settings';

/**
 * Stripe PaymentIntent metadata discriminator. The deposit webhook and refund
 * handlers use it to tell deposits apart from every other payment we create,
 * so it must stay byte-identical to the value already on live PaymentIntents.
 */
export const PATIENT_DEPOSIT_TYPE = 'patient-deposit';

export type PatientDepositType = typeof PATIENT_DEPOSIT_TYPE;

/**
 * The canonical lifecycle state of a deposit request.
 *
 * `Cancelled` means the request was abandoned before it earned an outcome —
 * superseded, waived, or cancelled while still pending. It deliberately does
 * NOT cover a hold that was released after attendance (`Released`) or a charge
 * that was returned to the patient (`Refunded`), because collapsing those into
 * `Cancelled` loses the single most useful fact when reconciling or supporting
 * a deposit. Ask questions about these values through `DepositLifecycle`
 * rather than comparing them inline.
 */
export enum DepositRequestStatus {
  Pending = 'pending',
  Authorised = 'authorised',
  Paid = 'paid',
  Expired = 'expired',
  Cancelled = 'cancelled',
  Released = 'released',
  Refunded = 'refunded',
}

/**
 * Who initiated a hold release. Kept separate from `DepositReleaseOutcome`
 * because the outcome states why releasing was legitimate, which is identical
 * whether staff or a trigger acted. Merging them would make the recorded value
 * depend on which path won a race.
 */
export const DEPOSIT_RELEASE_SOURCE = {
  Automatic: 'automatic',
  Staff: 'staff',
} as const;

export type DepositReleaseSource =
  (typeof DEPOSIT_RELEASE_SOURCE)[keyof typeof DEPOSIT_RELEASE_SOURCE];

export enum DepositRequestDeliveryChannel {
  Email = 'email',
  SMS = 'sms',
}

export enum DepositForfeitureOutcomeStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

/**
 * Outcome of a deposit request creation via the createDepositRequest callable.
 * Models the deliveryStatus returned by the HTTPS callable response.
 */
export enum DepositRequestOutcome {
  Accepted = 'accepted',
  Queued = 'queued',
  Failed = 'failed',
}

export const DEPOSIT_STAFF_ATTENTION_REASON = {
  UnactionedLiveMoney: 'unactioned-live-money',
  ReleaseFailed: 'release-failed',
  RefundCreditShortfall: 'refund-credit-shortfall',
  RefundFailed: 'refund-failed',
  HoldCannotCoverRescheduledAppointment:
    'hold-cannot-cover-rescheduled-appointment',
  HoldLapsedBeforeAppointment: 'hold-lapsed-before-appointment',
} as const;

export type DepositStaffAttentionReason =
  (typeof DEPOSIT_STAFF_ATTENTION_REASON)[keyof typeof DEPOSIT_STAFF_ATTENTION_REASON];

export const DEPOSIT_RELEASE_OUTCOME = {
  CompletedAttendance: 'completed-attendance',
  CancelledWithoutForfeiture: 'cancelled-without-forfeiture',
  /** Staff turned the booking request down, so the hold was never owed. */
  BookingRequestRejected: 'booking-request-rejected',
} as const;

export type DepositReleaseOutcome =
  (typeof DEPOSIT_RELEASE_OUTCOME)[keyof typeof DEPOSIT_RELEASE_OUTCOME];

export const DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON = {
  AuthorisationWindowTooShort: 'authorisation-window-too-short',
  AppointmentStartUnknown: 'appointment-start-unknown',
  NoBookingRequestToApprove: 'no-booking-request-to-approve',
} as const;

export type DepositCaptureStrategyFallbackReason =
  (typeof DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON)[keyof typeof DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON];

export const DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON_TEXT: Record<
  DepositCaptureStrategyFallbackReason,
  IDepositOptionText
> = {
  [DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.AuthorisationWindowTooShort]: {
    label: 'Configured strategy changed: appointment is too far away',
    description:
      'A card authorisation cannot be held long enough to reach this appointment, so the deposit uses the next safest capture strategy.',
  },
  [DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.AppointmentStartUnknown]: {
    label: 'Configured strategy changed: appointment time is unknown',
    description:
      'The appointment start time is missing, so the deposit uses a strategy that does not rely on holding the card until attendance.',
  },
  [DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON.NoBookingRequestToApprove]: {
    label: 'Configured strategy changed: no booking request to accept',
    description:
      'There is no booking request approval step for this deposit, so the deposit uses a strategy that can be completed without one.',
  },
};

export interface IDepositStaffAttention {
  reason: DepositStaffAttentionReason;
  flaggedAt: Timestamp;
}

export interface IDepositExpiryTask {
  name: string;
  scheduledFor: Timestamp;
}

export interface IDepositReleaseClaim {
  claimId: string;
  claimedAt: Timestamp;
  finalizedAt?: Timestamp;
}

interface IBaseDepositForfeitureOutcome {
  schedulingEventRef: DocumentReference<ISchedulingEvent>;
}

export type IDepositForfeitureOutcome = IBaseDepositForfeitureOutcome &
  (
    | {
        status: DepositForfeitureOutcomeStatus.Pending;
      }
    | {
        status: DepositForfeitureOutcomeStatus.Succeeded;
        amountCents: number;
        resolvedAt: Timestamp;
        /**
         * The Cancellation Fee invoice paid by the forfeited account credit
         * from the deposit. Optional because forfeitures recorded before fee
         * invoices existed have no invoice to point at.
         */
        feeInvoiceRef?: DocumentReference<IInvoice>;
      }
    | {
        status: DepositForfeitureOutcomeStatus.Failed;
        resolvedAt: Timestamp;
      }
  );

export interface IDepositConfirmationEmailPayload {
  readonly recipientEmail: string;
  readonly recipientFirstName: string;
  readonly recipientName: string;
  readonly practiceName: string;
  readonly treatmentName: string;
  readonly practitionerName?: string;
  readonly appointmentDate: string;
  readonly appointmentTime: string;
  readonly eventFrom: Timestamp;
}

export interface IDepositRequest extends ISoftDelete {
  practiceRef: DocumentReference<IPractice>;
  appointmentRef?: DocumentReference<IAppointment>;
  appointmentRequestRef?: DocumentReference<IAppointmentRequest>;
  patientRef?: DocumentReference<IPatient>;
  depositInvoiceRef?: DocumentReference<IInvoice>;
  paymentAttemptRef?: DocumentReference<IDepositPaymentAttempt>;
  treatmentName: string;
  amountCents: number;
  currency: Currency;
  paymentMode: DepositPaymentMode;
  captureStrategy?: DepositCaptureStrategy;
  treatmentConfigurationDeposits?: Record<
    string,
    IDepositTreatmentConfigurationContribution
  >;
  status: DepositRequestStatus;
  paidAt?: Timestamp;
  stripeMetadataLinkedAt?: Timestamp;
  stripeMetadataLinkPending?: boolean;
  stripeMetadataLinkClaimedAt?: Timestamp;
  stripeMetadataLinkAttempts?: number;
  /**
   * Durable email work lives outside the deposit state machine. Neither
   * transitionDepositRequest nor approval transactions may write or clear it.
   */
  confirmationEmailPayload?: IDepositConfirmationEmailPayload;
  confirmationEmailPending?: boolean;
  confirmationEmailSentAt?: Timestamp;
  confirmationEmailClaimedAt?: Timestamp;
  confirmationEmailAttempts?: number;
  lastPaymentError?: string;
  captureBefore?: Timestamp;
  capturedAt?: Timestamp;
  captureClaimedAt?: Timestamp;
  forfeitedAt?: Timestamp;
  forfeitureOutcome?: IDepositForfeitureOutcome;
  staffAttention?: IDepositStaffAttention;
  releasedAt?: Timestamp;
  releaseOutcome?: DepositReleaseOutcome;
  releaseSource?: DepositReleaseSource;
  releasedBy?: DocumentReference<IStaffer>;
  fallbackReason?: DepositCaptureStrategyFallbackReason;
  expiryTask?: IDepositExpiryTask;
  cumulativeRefundedCents?: number;
  releaseClaim?: IDepositReleaseClaim;
  expiresAt: Timestamp;
}

export const isDepositConfirmationEmailPayload =
  TypeGuard.interface<IDepositConfirmationEmailPayload>({
    recipientEmail: isString,
    recipientFirstName: isString,
    recipientName: isString,
    practiceName: isString,
    treatmentName: isString,
    practitionerName: TypeGuard.nilOr(isString),
    appointmentDate: isString,
    appointmentTime: isString,
    eventFrom: isTimestamp,
  });

export const isDepositRequest = TypeGuard.interface<IDepositRequest>({
  practiceRef: isDocRef,
  appointmentRef: TypeGuard.nilOr(isDocRef),
  appointmentRequestRef: TypeGuard.nilOr(isDocRef),
  patientRef: TypeGuard.nilOr(isDocRef),
  depositInvoiceRef: TypeGuard.nilOr(isDocRef),
  paymentAttemptRef: TypeGuard.nilOr(isDocRef),
  treatmentName: isString,
  amountCents: isNumber,
  currency: isString,
  paymentMode: TypeGuard.enumValue(DepositPaymentMode),
  captureStrategy: TypeGuard.nilOr(TypeGuard.enumValue(DepositCaptureStrategy)),
  treatmentConfigurationDeposits: TypeGuard.nilOr(
    isDepositTreatmentConfigurationContributionMap
  ),
  status: TypeGuard.enumValue(DepositRequestStatus),
  paidAt: TypeGuard.nilOr(isObject),
  stripeMetadataLinkedAt: TypeGuard.nilOr(isTimestamp),
  stripeMetadataLinkPending: TypeGuard.nilOr(isBoolean),
  stripeMetadataLinkClaimedAt: TypeGuard.nilOr(isTimestamp),
  stripeMetadataLinkAttempts: TypeGuard.nilOr(isNumber),
  confirmationEmailPayload: TypeGuard.nilOr(isDepositConfirmationEmailPayload),
  confirmationEmailPending: TypeGuard.nilOr(isBoolean),
  confirmationEmailSentAt: TypeGuard.nilOr(isTimestamp),
  confirmationEmailClaimedAt: TypeGuard.nilOr(isTimestamp),
  confirmationEmailAttempts: TypeGuard.nilOr(isNumber),
  lastPaymentError: TypeGuard.nilOr(isString),
  captureBefore: TypeGuard.nilOr(isTimestamp),
  capturedAt: TypeGuard.nilOr(isObject),
  captureClaimedAt: TypeGuard.nilOr(isObject),
  forfeitedAt: TypeGuard.nilOr(isObject),
  forfeitureOutcome: TypeGuard.nilOr(
    (data): data is IDepositForfeitureOutcome => {
      if (
        !isObject(data) ||
        !isDocRef(data.schedulingEventRef) ||
        !TypeGuard.enumValue(DepositForfeitureOutcomeStatus)(data.status)
      ) {
        return false;
      }
      switch (data.status) {
        case DepositForfeitureOutcomeStatus.Pending:
          return (
            data.amountCents === undefined && data.resolvedAt === undefined
          );
        case DepositForfeitureOutcomeStatus.Succeeded:
          return (
            TypeGuard.positiveInteger(data.amountCents) &&
            isTimestamp(data.resolvedAt) &&
            TypeGuard.nilOr(isDocRef)(data.feeInvoiceRef)
          );
        case DepositForfeitureOutcomeStatus.Failed:
          return data.amountCents === undefined && isTimestamp(data.resolvedAt);
        default:
          return false;
      }
    }
  ),
  staffAttention: TypeGuard.nilOr(
    TypeGuard.interface<IDepositStaffAttention>({
      reason: TypeGuard.enumValue(DEPOSIT_STAFF_ATTENTION_REASON),
      flaggedAt: isTimestamp,
    })
  ),
  releasedAt: TypeGuard.nilOr(isTimestamp),
  releaseOutcome: TypeGuard.nilOr(TypeGuard.enumValue(DEPOSIT_RELEASE_OUTCOME)),
  releaseSource: TypeGuard.nilOr(TypeGuard.enumValue(DEPOSIT_RELEASE_SOURCE)),
  releasedBy: TypeGuard.nilOr(isDocRef),
  fallbackReason: TypeGuard.nilOr(
    TypeGuard.enumValue(DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON)
  ),
  expiryTask: TypeGuard.nilOr(
    TypeGuard.interface<IDepositExpiryTask>({
      name: isString,
      scheduledFor: isTimestamp,
    })
  ),
  cumulativeRefundedCents: TypeGuard.nilOr(isNumber),
  releaseClaim: TypeGuard.nilOr(
    TypeGuard.interface<IDepositReleaseClaim>({
      claimId: isString,
      claimedAt: isTimestamp,
      finalizedAt: TypeGuard.nilOr(isTimestamp),
    })
  ),
  deleted: isBoolean,
  expiresAt: isObject,
});
